// Shared, non-React helpers for the attachments feature (XPEN-2374) — kept
// here rather than in a UI component so both AttachmentsPanel and any page
// that seeds an attachment count (e.g. invoice.js) can depend on plain
// functions instead of a component module.

export const buildAttachmentsListUrl = (ownerType, ownerId, ownerSubId) => {
  const params = new URLSearchParams({ owner_type: ownerType, owner_id: ownerId });
  if (ownerSubId) params.set('owner_sub_id', ownerSubId);
  return `/attachments?${params.toString()}`;
};

// Prefers the server-computed count; falls back to the list length if it's
// ever missing or malformed, since both describe the same thing.
export const resolveAttachmentsCount = (response) => {
  const { count, attachments } = response?.data ?? {};
  if (Number.isInteger(count) && count >= 0) return count;
  return Array.isArray(attachments) ? attachments.length : 0;
};

const formatAttachmentGroupAmount = (total) => {
  const amount = Number(total);
  if (!Number.isFinite(amount)) return null;
  return `$${amount.toFixed(2)}`;
};

// XPEN-3118 — checksum → label parts for XPENSE attachment groups.
// First ListItem wins when MatterSplits share one checksum. Shape:
// { lineNumber, amount, details } — the panel adds the attachment count
// and i18n "Line {n}" prefix at render time.
export const buildAttachmentGroupLabels = (listItems) => {
  const labels = {};
  if (!Array.isArray(listItems)) return labels;
  listItems.forEach((item, index) => {
    const checksum = item?.checksum;
    if (!checksum || Object.prototype.hasOwnProperty.call(labels, checksum)) return;
    const details = [item.WorkDate, item.Description]
      .filter((part) => typeof part === 'string' && part.trim())
      .map((part) => part.trim());
    labels[checksum] = {
      lineNumber: index + 1,
      amount: formatAttachmentGroupAmount(item.Total),
      details,
    };
  });
  return labels;
};

// Align with XPEN-3117 blank→null: whitespace-only is not a real group key.
const normalizeOwnerSubId = (value) => {
  const key = String(value || '').trim();
  return key || null;
};

// Group only when two or more distinct non-null owner_sub_id values exist
// (ticket rule). One receipt id + unassigned files stays flat.
export const shouldGroupAttachmentsByOwnerSubId = (attachments) => {
  const distinct = new Set();
  for (const attachment of attachments || []) {
    const key = normalizeOwnerSubId(attachment?.owner_sub_id);
    if (key) distinct.add(key);
  }
  return distinct.size > 1;
};

// Partition for grouped rendering. Unassigned (null/empty owner_sub_id) is
// returned separately so the panel can place that section at the bottom.
// Group order here is first-seen in the attachments payload; callers that
// have ListItem line numbers should sort with sortAttachmentGroupsByLineNumber.
export const partitionAttachmentsByOwnerSubId = (attachments) => {
  const groupsByKey = new Map();
  const unassigned = [];
  for (const attachment of attachments || []) {
    const key = normalizeOwnerSubId(attachment?.owner_sub_id);
    if (!key) {
      unassigned.push(attachment);
      continue;
    }
    if (!groupsByKey.has(key)) groupsByKey.set(key, []);
    groupsByKey.get(key).push(attachment);
  }
  return {
    groups: [...groupsByKey.entries()].map(([key, items]) => ({ key, items })),
    unassigned,
  };
};

// Match GL Detail / ListItem order (Line 1, Line 2, …). Groups without a
// known lineNumber stay after numbered ones, in their relative order.
export const sortAttachmentGroupsByLineNumber = (groups, attachmentGroupLabels) => {
  if (!Array.isArray(groups) || groups.length < 2) return groups || [];
  return [...groups].sort((left, right) => {
    const lineLeft = attachmentGroupLabels?.[left.key]?.lineNumber;
    const lineRight = attachmentGroupLabels?.[right.key]?.lineNumber;
    const rankLeft = Number.isInteger(lineLeft) ? lineLeft : Number.MAX_SAFE_INTEGER;
    const rankRight = Number.isInteger(lineRight) ? lineRight : Number.MAX_SAFE_INTEGER;
    return rankLeft - rankRight;
  });
};
